/**
 * ThemeProvider — owns the dark/light choice for the dashboard.
 *
 * Strategy:
 *   - The user's *preference* is one of 'light' | 'dark' | 'system'.
 *   - 'system' resolves live against `prefers-color-scheme`.
 *   - The resolved theme ('light' | 'dark') is written to `data-theme` on
 *     <html>, which is what Tailwind's `@custom-variant dark` keys off of.
 *   - The preference persists in localStorage under `hotline.theme`.
 *
 * Defaults to dark (the app's historical look) when nothing is stored.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'hotline.theme';

export interface ThemeContextValue {
  /** The user's stored preference (may be 'system'). */
  preference: ThemePreference;
  /** The concrete theme currently applied to the document. */
  theme: ResolvedTheme;
  /** Set a specific preference. */
  setPreference: (next: ThemePreference) => void;
  /** Flip between light and dark (resolving 'system' first). */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'dark';
  const raw = window.localStorage?.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'dark';
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return prefersDark() ? 'dark' : 'light';
  return preference;
}

function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** Override the initial preference (tests). */
  defaultPreference?: ThemePreference;
}

export function ThemeProvider({
  children,
  defaultPreference,
}: ThemeProviderProps): ReactElement {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => defaultPreference ?? readStoredPreference(),
  );
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolve(preference));

  // Apply the resolved theme to <html> whenever the preference changes, and keep
  // it in sync with the OS when the preference is 'system'.
  useEffect(() => {
    const next = resolve(preference);
    setTheme(next);
    applyTheme(next);

    if (preference !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      const resolved: ResolvedTheme = media.matches ? 'dark' : 'light';
      setTheme(resolved);
      applyTheme(resolved);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference): void => {
    setPreferenceState(next);
    try {
      window.localStorage?.setItem(STORAGE_KEY, next);
    } catch {
      /* storage may be unavailable (private mode); ignore */
    }
  }, []);

  const toggle = useCallback((): void => {
    setPreferenceState((prev) => {
      const current = resolve(prev);
      const next: ThemePreference = current === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage?.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, theme, setPreference, toggle }),
    [preference, theme, setPreference, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a <ThemeProvider>');
  }
  return ctx;
}
